import { useMemo, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { useLocation } from "react-router-dom";
import { useUserContext } from "../hooks/UserContext";
import { fetchHistorialPrecios } from "../services/reporteService";
import DateRangePicker from "./components/DateRangePickerComponent";
import LocalSelector from "./components/LocalSelector";

type FiltrosFecha = { startDate: Dayjs | null; endDate: Dayjs | null };
type HistorialRow = Record<string, unknown> & { id: number };

const pageSize = 50;

const ReporteHistorialPrecios = () => {
  const location = useLocation();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialLocal = query.get("idLocal") ?? undefined;
  const initialDesde = query.get("fechaDesde");
  const initialHasta = query.get("fechaHasta");
  const initialDates: FiltrosFecha = {
    startDate: initialDesde && dayjs(initialDesde).isValid() ? dayjs(initialDesde) : null,
    endDate: initialHasta && dayjs(initialHasta).isValid() ? dayjs(initialHasta) : null,
  };

  const { getUser } = useUserContext();
  const [idLocal, setIdLocal] = useState<string | undefined>(initialLocal);
  const [rango, setRango] = useState<FiltrosFecha>(initialDates);
  const [filas, setFilas] = useState<HistorialRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);

  const columnas = useMemo<GridColDef[]>(() => {
    const primeraFila = filas[0];
    if (!primeraFila) return [];

    return Object.keys(primeraFila)
      .filter((campo) => campo !== "id")
      .map((campo) => ({ field: campo, headerName: campo, minWidth: 145, flex: 1 }));
  }, [filas]);

  const consultar = async (nuevaPagina = 0) => {
    const usuario = getUser();
    if (!usuario || !idLocal || !rango.startDate || !rango.endDate) {
      setError("Elegí un local y las dos fechas para consultar.");
      return;
    }

    setCargando(true);
    setError(null);
    try {
      const resultado = await fetchHistorialPrecios(
        usuario,
        Number(idLocal),
        rango.startDate,
        rango.endDate,
        nuevaPagina,
        pageSize
      );
      setFilas(resultado.filas.map((fila, indice) => ({ ...fila, id: nuevaPagina * pageSize + indice })));
      setTotal(resultado.total);
      setPagina(nuevaPagina);
    } catch (exception) {
      setFilas([]);
      setTotal(0);
      setError(exception instanceof Error ? exception.message : "No se pudo consultar el historial.");
    } finally {
      setCargando(false);
    }
  };

  const cambiarPaginacion = (modelo: GridPaginationModel) => {
    if (modelo.page !== pagina) void consultar(modelo.page);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Historial de precios</Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Consulta de cambios registrados en precios de compra y venta para el local elegido. Es de solo lectura.
      </Alert>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
          <Box sx={{ flex: 1, width: "100%" }}>
            <LocalSelector valorInicial={initialLocal} onLocalChange={setIdLocal} />
          </Box>
          <Box sx={{ flex: 2, width: "100%" }}>
            <DateRangePicker initialValue={initialDates} onDateChange={setRango} />
          </Box>
          <Button variant="contained" onClick={() => void consultar(0)} disabled={cargando}>
            {cargando ? <CircularProgress size={20} color="inherit" /> : "Consultar"}
          </Button>
        </Stack>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper variant="outlined" sx={{ height: 560, width: "100%" }}>
        <DataGrid
          rows={filas}
          columns={columnas}
          rowCount={total}
          loading={cargando}
          paginationMode="server"
          paginationModel={{ page: pagina, pageSize }}
          onPaginationModelChange={cambiarPaginacion}
          pageSizeOptions={[pageSize]}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: "Sin resultados para los filtros seleccionados" }}
        />
      </Paper>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {total} cambios en el período seleccionado.
      </Typography>
    </Box>
  );
};

export default ReporteHistorialPrecios;
